import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { UserCheck, Check, X } from 'lucide-react';
import { toast } from 'sonner';

import { errMsg } from '../lib/errors';
export default function WholesaleApprovals() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [action, setAction] = useState(null); // 'approve' | 'reject'
    const [level, setLevel] = useState('1');
    const [notes, setNotes] = useState('');

    const load = async () => {
        setLoading(true);
        try { const r = await api.get('/customers/wholesale/pending'); setItems(r.data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openApprove = (c) => { setSelected(c); setAction('approve'); setLevel(String(c.requested_level || 1)); setNotes(''); };
    const openReject = (c) => { setSelected(c); setAction('reject'); setNotes(''); };

    const submit = async () => {
        try {
            if (action === 'approve') {
                await api.post(`/customers/${selected.id}/approve`, { level: parseInt(level), notes });
                toast.success(`Approved as Level ${level}`);
            } else {
                if (!notes) { toast.error('Please provide reason'); return; }
                await api.post(`/customers/${selected.id}/reject`, { reason: notes });
                toast.success('Application rejected');
            }
            setSelected(null); setAction(null); load();
        } catch (e) { toast.error(errMsg(e, 'Failed')); }
    };

    return (
        <AppShell pageTitle="Wholesale Approvals">
            {loading ? <LoadingBlock /> : items.length === 0 ? (
                <EmptyState icon={UserCheck} title="No pending approvals" description="All wholesale applications are reviewed." />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="wholesale-approvals-list">
                    {items.map(c => (
                        <Card key={c.id} className="p-4" data-testid={`wholesale-app-${c.id}`}>
                            <div className="flex items-start justify-between mb-3">
                                <div>
                                    <div className="font-semibold">{c.business_name}</div>
                                    <div className="text-xs text-neutral-500">Applied {new Date(c.created_at).toLocaleDateString()}</div>
                                </div>
                                <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-800 px-2.5 py-1 text-xs font-medium">Pending L{c.requested_level}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                                <div><span className="text-neutral-500">Contact:</span> {c.name}</div>
                                <div><span className="text-neutral-500">Mobile:</span> {c.mobile}</div>
                                <div><span className="text-neutral-500">Email:</span> {c.email || '—'}</div>
                                <div><span className="text-neutral-500">Type:</span> {c.business_type || '—'}</div>
                                <div><span className="text-neutral-500">GSTIN:</span> {c.gstin || '—'}</div>
                                <div><span className="text-neutral-500">PAN:</span> {c.pan || '—'}</div>
                                <div className="col-span-2"><span className="text-neutral-500">Address:</span> {c.business_address}</div>
                                <div className="col-span-2"><span className="text-neutral-500">Location:</span> {c.location?.state} · {c.location?.pincode}</div>
                                {c.notes && <div className="col-span-2 mt-1 p-2 bg-neutral-50 rounded text-neutral-700">Note: {c.notes}</div>}
                            </div>
                            <div className="flex gap-2 mt-4">
                                <Button className="flex-1" onClick={() => openApprove(c)} data-testid={`approve-${c.id}`} style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                                    <Check className="h-4 w-4 mr-1" />Approve
                                </Button>
                                <Button variant="outline" onClick={() => openReject(c)} data-testid={`reject-${c.id}`}>
                                    <X className="h-4 w-4 mr-1" />Reject
                                </Button>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            <Dialog open={!!action} onOpenChange={(v) => { if (!v) { setAction(null); setSelected(null); } }}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{action === 'approve' ? 'Approve Wholesale' : 'Reject Wholesale'} — {selected?.business_name}</DialogTitle></DialogHeader>
                    {action === 'approve' && (
                        <div className="space-y-3">
                            <div>
                                <Label>Assign Level</Label>
                                <Select value={level} onValueChange={setLevel}>
                                    <SelectTrigger data-testid="approval-level-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {[1, 2, 3, 4, 5].map(l => <SelectItem key={l} value={String(l)}>Level {l}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <div className="text-xs text-neutral-500 mt-1">Customer requested Level {selected?.requested_level}</div>
                            </div>
                            <div><Label>Approval Notes (optional)</Label><Textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} data-testid="approval-notes-input" /></div>
                        </div>
                    )}
                    {action === 'reject' && (
                        <div><Label>Rejection Reason</Label><Textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} data-testid="rejection-reason-input" placeholder="Explain why this application is rejected" /></div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setAction(null); setSelected(null); }}>Cancel</Button>
                        <Button onClick={submit} data-testid="confirm-approval-button"
                            style={{ background: action === 'approve' ? 'hsl(142 71% 45%)' : 'hsl(0 84% 60%)', color: 'white' }}>
                            {action === 'approve' ? 'Approve' : 'Reject'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
